/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { ReactNode, useMemo } from "react";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElementLocale, StripeElementsOptions } from "@stripe/stripe-js";
import UIConstants from "../../../UIConstants";
import { getCountryGermany } from "../../Internationalisierung/Country";
import { useAppContext } from "./../AppContext";

export interface StripeProps {
    children: ReactNode;
    clientSecret?: string;
    stripeAccountId?: string;
}

// Dokumentation zu React Stripe.js
//     https://stripe.com/docs/stripe-js/react
// Zukünftige Zahlungen einrichten
//     https://stripe.com/docs/payments/save-and-reuse?html-or-react=react
// React + Stripe - Accept payment through PaymentIntent API and save card details
//     https://javascript.plainenglish.io/react-stripe-accept-payment-through-paymentintent-api-and-save-card-details-aa90c55fcdb2
// Integrating Stripe in your React app with React Stripe.js
//     https://blog.logrocket.com/integrating-stripe-react-stripe-js/
// CodeSandbox mit Beispielen von Stripe
//     https://codesandbox.io/s/react-stripe-official-q1loc?fontsize=14&hidenavigation=1&theme=dark
// This example shows you how to set up React Stripe.js and use Elements.
//     https://github.com/stripe/react-stripe-js/blob/master/examples/hooks/1-Card-Detailed.js
// Stripe Payment Element
//     https://stripe.com/docs/payments/payment-element
// Aktualisieren von Kartenangaben mit einem SetupIntent
//     https://support.stripe.com/questions/update-card-details-with-a-setupintent
// How To Call Child Function From Parent Component In React
//     https://dilshankelsen.com/call-child-function-from-parent-component-in-react/
// Elements Appearance API - Customize the look and feel of Elements to match the design of your site.
//     https://stripe.com/docs/stripe-js/appearance-api
export default function StripeElements(props: StripeProps) {
    const stripeOptions = useAppContext().options.stripe;
    const stripePromise: Promise<Stripe | null> = useMemo(() => {
        return loadStripe(stripeOptions.publicKey, { stripeAccount: props.stripeAccountId });
    }, [stripeOptions.publicKey, props.stripeAccountId]);

    const stripeElementsOptions: StripeElementsOptions = useMemo(() => {
        const locale: StripeElementLocale = UIConstants.kulturShortLower as StripeElementLocale ?? getCountryGermany().languageCode;
        return {
            locale,
            clientSecret: props.clientSecret,
        };
    }, [props.clientSecret]);

    return (
        <Elements stripe={stripePromise} options={stripeElementsOptions}>
            {props.children}
        </Elements>
    );
}
